/* eslint-disable no-param-reassign */
/* eslint-disable fp/no-mutation */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import {
  Credential,
  ExternalCertificate,
  IBSCCertification,
  LearningPath,
  License,
  LinkedService,
  LiveCertificate,
  RecordedCertificate,
  User,
} from 'src/v3/core/types'
import axios, { pluckData } from 'src/utilities/http-client'

type AssignmentWithCourses = {
  id: string
  name: string
  status: 'Complete' | 'Incomplete'
  dueDate: string
  dateCompleted: string
  linkedServiceID: string
  courses: {
    id: string
    name: string
    status: 'Complete' | 'Incomplete'
    finalDate: string
  }[]
}

type CourseStatus = {
  id: string
  name: string
  imageName: string
  status: 'Complete' | 'Incomplete'
}

export type NewYorkCourseStatus = CourseStatus & {
  mandatoryStatus?: 'Mandatory' | 'Core Content'
  topicArea?: string
}

export let fetchUser = createAsyncThunk('profile/fetchUserStatus', () =>
  axios.get<User>('/api/v2/profile/user').then(pluckData),
)

export let fetchAssignments = createAsyncThunk(
  'profile/fetchAssignmentsStatus',
  () =>
    axios
      .get<AssignmentWithCourses[]>('/api/v3/profile/assignments-with-courses')
      .then(pluckData)
)

export let fetchCertificates = createAsyncThunk(
  'profile/fetchCertificatesStatus',
  () =>
    axios
      .get<RecordedCertificate[]>('/api/v2/profile/certificates')
      .then(pluckData),
)

export let fetchLiveCertificates = createAsyncThunk(
  'profile/fetchLiveCertificatesStatus',
  () =>
    axios
      .get<LiveCertificate[]>('/api/v2/profile/live-class-certificates')
      .then(pluckData),
)

export let fetchExternalCertificates = createAsyncThunk(
  'profile/fetchExternalCertificatesStatus',
  () =>
    axios
      .get<ExternalCertificate[]>('/api/v2/profile/external-certificates')
      .then(pluckData),
)

export let fetchCredentials = createAsyncThunk(
  'profile/fetchCredentialsStatus',
  () => axios.get<Credential[]>('/api/v2/profile/credentials').then(pluckData),
)

export let fetchLicenses = createAsyncThunk('profile/fetchLicensesStatus', () =>
  axios.get<License[]>('/api/profile/licenses').then(pluckData),
)

export let fetchIBSC100Courses = createAsyncThunk(
  'profile/fetchIBSC100CoursesStatus',
  () =>
    axios
      .get<CourseStatus[]>('/api/v2/profile/ibsc100/courses')
      .then(pluckData),
)

export let fetchIBSC40Courses = createAsyncThunk(
  'profile/fetchIBSC40CoursesStatus',
  () =>
    axios.get<CourseStatus[]>('/api/v2/profile/ibsc40/courses').then(pluckData),
)

export let fetchLearningPaths = createAsyncThunk(
  'profile/fetchLearningPathsStatus',
  () =>
    axios.get<LearningPath[]>('/api/v2/profile/learning-paths').then(pluckData),
)

export let fetchLearningPathCourses = createAsyncThunk(
  'profile/fetchLearningPathCoursesStatus',
  (id: string) =>
    axios
      .get<
        CourseStatus[]
      >(`/api/v3/profile/learning-paths/courses?learningPathID=${id}`)
      .then(pluckData),
)

export let fetchNewYorkLearningPathCourses = createAsyncThunk(
  'profile/fetchNewYorkLearningPathCoursesStatus',
  (id: string) =>
    axios
      .get<
        NewYorkCourseStatus[]
      >(`/api/profile/learning-path/courses/new-york?learningPathID=${id}`)
      .then(pluckData),
)

export let fetchCompletedLearningPaths = createAsyncThunk(
  'profile/fetchCompletedLearningPathsStatus',
  () =>
    axios
      .get<LearningPath[]>('/api/v2/profile/learning-paths-for-user')
      .then(pluckData),
)

export let fetchLinkedServices = createAsyncThunk(
  'profile/fetchLinkedServicesStatus',
  () =>
    axios
      .get<LinkedService[]>('/api/v2/profile/linked-services')
      .then(pluckData),
)

export let fetchIBSC100Certification = createAsyncThunk(
  'profile/fetchIBSC100CertificationStatus',
  () =>
    axios
      .get<IBSCCertification>('/api/v2/profile/ibsc100/certification')
      .then(pluckData),
)

export let fetchIBSC40Certification = createAsyncThunk(
  'profile/fetchIBSC40CertificationStatus',
  () =>
    axios
      .get<IBSCCertification>('/api/v2/profile/ibsc40/certification')
      .then(pluckData),
)

type ProfileState = {
  user?: User
  userStatus: Status
  assignments: AssignmentWithCourses[]
  assignmentsStatus: Status
  certificates: RecordedCertificate[]
  certificatesStatus: Status
  liveCertificates: LiveCertificate[]
  liveCertificatesStatus: Status
  externalCertificates: ExternalCertificate[]
  externalCertificatesStatus: Status
  credentials: Credential[]
  credentialsStatus: Status
  licenses: License[]
  licensesStatus: Status
  ibsc100Courses: CourseStatus[]
  ibsc100CoursesStatus: Status
  ibsc40Courses: CourseStatus[]
  ibsc40CoursesStatus: Status
  learningPaths: LearningPath[]
  learningPathsStatus: Status
  learningPathCourses: Record<string, CourseStatus[]>
  learningPathCoursesStatus: Record<string, Status>
  newYorkLearningPathCourses: Record<string, NewYorkCourseStatus[]>
  newYorkLearningPathCoursesStatus: Record<string, Status>
  completedLearningPaths: LearningPath[]
  completedLearningPathsStatus: Status
  linkedServices: LinkedService[]
  linkedServicesStatus: Status
  ibsc100Certification?: IBSCCertification
  ibsc100CertificationStatus: Status
  ibsc40Certification?: IBSCCertification
  ibsc40CertificationStatus: Status
}

export let initialState: ProfileState = {
  userStatus: 'uninitialized',
  assignments: [],
  assignmentsStatus: 'uninitialized',
  certificates: [],
  certificatesStatus: 'uninitialized',
  liveCertificates: [],
  liveCertificatesStatus: 'uninitialized',
  externalCertificates: [],
  externalCertificatesStatus: 'uninitialized',
  credentials: [],
  credentialsStatus: 'uninitialized',
  licenses: [],
  licensesStatus: 'uninitialized',
  ibsc100Courses: [],
  ibsc100CoursesStatus: 'uninitialized',
  ibsc40Courses: [],
  ibsc40CoursesStatus: 'uninitialized',
  learningPaths: [],
  learningPathsStatus: 'uninitialized',
  learningPathCourses: {},
  learningPathCoursesStatus: {},
  newYorkLearningPathCourses: {},
  newYorkLearningPathCoursesStatus: {},
  completedLearningPaths: [],
  completedLearningPathsStatus: 'uninitialized',
  linkedServices: [],
  linkedServicesStatus: 'uninitialized',
  ibsc100CertificationStatus: 'uninitialized',
  ibsc40CertificationStatus: 'uninitialized',
}

export let profileSlice = createSlice({
  name: 'create',
  initialState,
  reducers: {
    learningPathsChanged: (state) => {
      state.learningPaths = []
      state.learningPathsStatus = 'uninitialized'
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.userStatus = 'pending'
      })
      .addCase(fetchUser.rejected, (state) => {
        state.userStatus = 'rejected'
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.userStatus = 'fulfilled'
        state.user = action.payload
      })
      .addCase(fetchAssignments.pending, (state) => {
        state.assignmentsStatus = 'pending'
      })
      .addCase(fetchAssignments.rejected, (state) => {
        state.assignmentsStatus = 'rejected'
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.assignmentsStatus = 'fulfilled'
        state.assignments = action.payload
      })
      .addCase(fetchCertificates.pending, (state) => {
        state.certificatesStatus = 'pending'
      })
      .addCase(fetchCertificates.rejected, (state) => {
        state.certificatesStatus = 'rejected'
      })
      .addCase(fetchCertificates.fulfilled, (state, action) => {
        state.certificatesStatus = 'fulfilled'
        state.certificates = action.payload
      })
      .addCase(fetchLiveCertificates.pending, (state) => {
        state.liveCertificatesStatus = 'pending'
      })
      .addCase(fetchLiveCertificates.rejected, (state) => {
        state.liveCertificatesStatus = 'rejected'
      })
      .addCase(fetchLiveCertificates.fulfilled, (state, action) => {
        state.liveCertificatesStatus = 'fulfilled'
        state.liveCertificates = action.payload
      })
      .addCase(fetchExternalCertificates.pending, (state) => {
        state.externalCertificatesStatus = 'pending'
      })
      .addCase(fetchExternalCertificates.rejected, (state) => {
        state.externalCertificatesStatus = 'rejected'
      })
      .addCase(fetchExternalCertificates.fulfilled, (state, action) => {
        state.externalCertificatesStatus = 'fulfilled'
        state.externalCertificates = action.payload
      })
      .addCase(fetchCredentials.pending, (state) => {
        state.credentialsStatus = 'pending'
      })
      .addCase(fetchCredentials.rejected, (state) => {
        state.credentialsStatus = 'rejected'
      })
      .addCase(fetchCredentials.fulfilled, (state, action) => {
        state.credentialsStatus = 'fulfilled'
        state.credentials = action.payload
      })
      .addCase(fetchLicenses.pending, (state) => {
        state.licensesStatus = 'pending'
      })
      .addCase(fetchLicenses.rejected, (state) => {
        state.licensesStatus = 'rejected'
      })
      .addCase(fetchLicenses.fulfilled, (state, action) => {
        state.licensesStatus = 'fulfilled'
        state.licenses = action.payload
      })
      .addCase(fetchIBSC100Courses.pending, (state) => {
        state.ibsc100CoursesStatus = 'pending'
      })
      .addCase(fetchIBSC100Courses.rejected, (state) => {
        state.ibsc100CoursesStatus = 'rejected'
      })
      .addCase(fetchIBSC100Courses.fulfilled, (state, action) => {
        state.ibsc100CoursesStatus = 'fulfilled'
        state.ibsc100Courses = action.payload
      })
      .addCase(fetchIBSC40Courses.pending, (state) => {
        state.ibsc40CoursesStatus = 'pending'
      })
      .addCase(fetchIBSC40Courses.rejected, (state) => {
        state.ibsc40CoursesStatus = 'rejected'
      })
      .addCase(fetchIBSC40Courses.fulfilled, (state, action) => {
        state.ibsc40CoursesStatus = 'fulfilled'
        state.ibsc40Courses = action.payload
      })
      .addCase(fetchLearningPaths.pending, (state) => {
        state.learningPathsStatus = 'pending'
      })
      .addCase(fetchLearningPaths.rejected, (state) => {
        state.learningPathsStatus = 'rejected'
      })
      .addCase(fetchLearningPaths.fulfilled, (state, action) => {
        state.learningPathsStatus = 'fulfilled'
        state.learningPaths = action.payload
      })
      .addCase(fetchLearningPathCourses.pending, (state, action) => {
        state.learningPathCoursesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchLearningPathCourses.rejected, (state, action) => {
        state.learningPathCoursesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchLearningPathCourses.fulfilled, (state, action) => {
        state.learningPathCoursesStatus[action.meta.arg] = 'fulfilled'
        state.learningPathCourses[action.meta.arg] = action.payload
      })
      .addCase(fetchNewYorkLearningPathCourses.pending, (state, action) => {
        state.newYorkLearningPathCoursesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchNewYorkLearningPathCourses.rejected, (state, action) => {
        state.newYorkLearningPathCoursesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchNewYorkLearningPathCourses.fulfilled, (state, action) => {
        state.newYorkLearningPathCoursesStatus[action.meta.arg] = 'fulfilled'
        state.newYorkLearningPathCourses[action.meta.arg] = action.payload
      })
      .addCase(fetchCompletedLearningPaths.pending, (state) => {
        state.completedLearningPathsStatus = 'pending'
      })
      .addCase(fetchCompletedLearningPaths.rejected, (state) => {
        state.completedLearningPathsStatus = 'rejected'
      })
      .addCase(fetchCompletedLearningPaths.fulfilled, (state, action) => {
        state.completedLearningPathsStatus = 'fulfilled'
        state.completedLearningPaths = action.payload
      })
      .addCase(fetchLinkedServices.pending, (state) => {
        state.linkedServicesStatus = 'pending'
      })
      .addCase(fetchLinkedServices.rejected, (state) => {
        state.linkedServicesStatus = 'rejected'
      })
      .addCase(fetchLinkedServices.fulfilled, (state, action) => {
        state.linkedServicesStatus = 'fulfilled'
        state.linkedServices = action.payload
      })
      .addCase(fetchIBSC100Certification.pending, (state) => {
        state.ibsc100CertificationStatus = 'pending'
      })
      .addCase(fetchIBSC100Certification.rejected, (state) => {
        state.ibsc100CertificationStatus = 'rejected'
      })
      .addCase(fetchIBSC100Certification.fulfilled, (state, action) => {
        state.ibsc100CertificationStatus = 'fulfilled'
        state.ibsc100Certification = action.payload
      })
      .addCase(fetchIBSC40Certification.pending, (state) => {
        state.ibsc40CertificationStatus = 'pending'
      })
      .addCase(fetchIBSC40Certification.rejected, (state) => {
        state.ibsc40CertificationStatus = 'rejected'
      })
      .addCase(fetchIBSC40Certification.fulfilled, (state, action) => {
        state.ibsc40CertificationStatus = 'fulfilled'
        state.ibsc40Certification = action.payload
      })
  },
})

export let { learningPathsChanged } = profileSlice.actions

export let selectUser = (state: AppRootState) => state.profile.user
export let selectUserStatus = (state: AppRootState) => state.profile.userStatus

export let selectAssignments = (state: AppRootState) =>
  state.profile.assignments.map((ca) => ({
    ...ca,
    name: ca.name || 'Unnamed Assignment',
  }))
export let selectAssignmentsStatus = (state: AppRootState) =>
  state.profile.assignmentsStatus

export let selectCertificates = (state: AppRootState) =>
  state.profile.certificates
export let selectCertificatesStatus = (state: AppRootState) =>
  state.profile.certificatesStatus

export let selectLiveCertificates = (state: AppRootState) =>
  state.profile.liveCertificates
export let selectLiveCertificatesStatus = (state: AppRootState) =>
  state.profile.liveCertificatesStatus

export let selectExternalCertificates = (state: AppRootState) =>
  state.profile.externalCertificates
export let selectExternalCertificatesStatus = (state: AppRootState) =>
  state.profile.externalCertificatesStatus

export let selectCredentials = (state: AppRootState) =>
  state.profile.credentials.map((c) => ({
    ...c,
    credentialType: typeof c.type === 'string' ? c.type : c.type.other,
  }))
export let selectCredentialsStatus = (state: AppRootState) =>
  state.profile.credentialsStatus

export let selectLicenses = (state: AppRootState) => state.profile.licenses
export let selectLicensesStatus = (state: AppRootState) =>
  state.profile.licensesStatus

export let selectIBSC100Courses = (state: AppRootState) =>
  state.profile.ibsc100Courses
export let selectIBSC100CoursesStatus = (state: AppRootState) =>
  state.profile.ibsc100CoursesStatus

export let selectIBSC40Courses = (state: AppRootState) =>
  state.profile.ibsc40Courses
export let selectIBSC40CoursesStatus = (state: AppRootState) =>
  state.profile.ibsc40CoursesStatus

export let selectLearningPaths = (state: AppRootState) =>
  state.profile.learningPaths
export let selectLearningPathsStatus = (state: AppRootState) =>
  state.profile.learningPathsStatus

export let selectLearningPathCourses = (state: AppRootState) =>
  state.profile.learningPathCourses
export let selectLearningPathCoursesStatus = (state: AppRootState) =>
  state.profile.learningPathCoursesStatus

export let selectNewYorkLearningPathCourses = (state: AppRootState) =>
  state.profile.newYorkLearningPathCourses
export let selectNewYorkLearningPathCoursesStatus = (state: AppRootState) =>
  state.profile.newYorkLearningPathCoursesStatus

export let selectCompletedLearningPaths = (state: AppRootState) =>
  state.profile.completedLearningPaths
export let selectCompletedLearningPathsStatus = (state: AppRootState) =>
  state.profile.completedLearningPathsStatus

export let selectLinkedServices = (state: AppRootState) =>
  state.profile.linkedServices
export let selectLinkedServicesStatus = (state: AppRootState) =>
  state.profile.linkedServicesStatus

export let selectIBSC100Certification = (state: AppRootState) =>
  state.profile.ibsc100Certification
export let selectIBSC100CertificationStatus = (state: AppRootState) =>
  state.profile.ibsc100CertificationStatus

export let selectIBSC40Certification = (state: AppRootState) =>
  state.profile.ibsc40Certification
export let selectIBSC40CertificationStatus = (state: AppRootState) =>
  state.profile.ibsc40CertificationStatus

export default profileSlice.reducer
